import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getPlayers, getOpponents, getScenarios, getLoadScenarios, getMatchDrills, getGPS, getPlayerPositions } from '../api';
import MatchChronicPanel from './gps/MatchChronicPanel';

// Einstein Phase 1, Task 3 proof-of-concept test route (/einstein-compose-test). Proves the
// `compose` tier's actual pattern: fetch the shared reference data ONCE, then render N
// independent report panels from resolved player ids — not N separate page loads, not
// duplicated fetches, not regenerated content. Two players by default (?a=<id>&b=<id> to pick
// specific ones); the panel itself (MatchChronicPanel.js) is the reusable unit a real `compose`
// response would reference by registry key + resolved params.
export default function ComposeTestMatchChronic() {
  const [searchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    Promise.all([getPlayers(), getOpponents(), getScenarios(), getLoadScenarios(), getMatchDrills(), getGPS(), getPlayerPositions()])
      .then(([pl, opps, scens, ct, drills, sessions, posHistory]) => {
        const sessionById = {};
        (sessions || []).forEach(s => { sessionById[s.id] = s; });
        const allMatchDrills = (drills || []).map(d => {
          const s = sessionById[d.session_id];
          return { ...d, session_date: s?.session_date, session_title: s?.session_title, opponent_id: s?.opponent_id };
        });
        setData({
          players: (pl || []).filter(p => p.active !== false),
          opponents: opps || [],
          scenarios: (scens || []).map(s => ({ ...s, startDate: s.start_date?.slice(0, 10), playerId: s.player_id })),
          customTemplates: ct || [],
          positions: (posHistory || []).map(p => ({ ...p, startDate: p.start_date?.slice(0, 10), playerId: p.player_id })),
          allMatchDrills,
        });
      })
      .catch(err => setError(err.message || 'Failed to load'));
  }, []);

  if (error) return <div style={{ padding: 24, color: '#f87171' }}>{error}</div>;
  if (!data) return <div style={{ padding: 24, color: 'var(--text-dimmed)' }}>Loading…</div>;

  const idParamA = searchParams.get('a');
  const idParamB = searchParams.get('b');
  const fallbackIds = data.players.slice(0, 2).map(p => p.id);
  const playerIdA = idParamA || fallbackIds[0];
  const playerIdB = idParamB || fallbackIds[1];

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg-app)', padding: 32 }}>
      <div style={{ maxWidth: 900, margin: '0 auto' }}>
        <h1 style={{ fontSize: 18, fontWeight: 800, color: 'var(--text-primary)', marginBottom: 4 }}>
          Compose proof-of-concept — Chronic Match Report
        </h1>
        <p style={{ fontSize: 12, color: 'var(--text-dimmed)', marginBottom: 20 }}>
          One shared fetch, two independent report panels — the pattern a real <code>compose</code> response assembles.
        </p>
        <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
          <MatchChronicPanel playerId={playerIdA} {...data} />
          <MatchChronicPanel playerId={playerIdB} {...data} />
        </div>
      </div>
    </div>
  );
}
